import { useApolloClient, useMutation } from "@apollo/client";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { EmailArrayField } from "components/Forms/EmailArrayField";
import { ManifestRequestFormat, WasteShipmentStatus } from "generated-graphql/graphql";
import { Control, Controller, FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { openInNewTab } from "util/openInNewTab";

import { formatQuantity, UNIT_LABELS } from "../Containers/containerDisplay";
import {
  EMAIL_MANIFEST_REQUEST_MUTATION,
  GENERATE_MANIFEST_REQUEST_MUTATION,
  MARK_SHIPMENT_REQUESTED_MUTATION,
} from "./api";
import { REFETCH, Shipment } from "./shipmentDetailTypes";
import { formatTotalsByUnit } from "./shipmentDisplay";

/**
 * PDF leads: it is the one a vendor can print and file as-is, and the
 * spreadsheets exist for anyone who wants to work the numbers.
 */
const REQUEST_FORMAT_OPTIONS: { value: ManifestRequestFormat; label: string }[] = [
  { value: ManifestRequestFormat.Pdf, label: "PDF" },
  { value: ManifestRequestFormat.Csv, label: "CSV" },
  { value: ManifestRequestFormat.Xlsx, label: "Excel (XLSX)" },
];

/**
 * The vendor-facing half of the shipment: produce the manifest request, send
 * it, or record that it was arranged some other way.
 *
 * All three actions move a draft to Requested, which is why they live together
 * — the request is a single milestone reached by three routes, not three
 * separate states.
 */
/** How the vendor is told about the pickup. One decision, three routes. */
type RequestRoute = "email" | "download" | "elsewhere";

/** The confirm button names the route's outcome, so it is keyed off the route. */
const PRIMARY_ACTION_LABEL: Record<RequestRoute, string> = {
  email: "Send request",
  download: "Download request",
  elsewhere: "Mark as requested",
};

type RequestPickupFormValues = {
  route: RequestRoute;
  format: ManifestRequestFormat;
  recipients: string[];
};

/** Rows in the vendor preview are one stream per unit, keyed the same way. */
function streamRowKey(line: Shipment["manifestRequestLines"][number]): string {
  return line.wasteStream?.id ?? "uncharacterized";
}

/**
 * The format choice belongs to the route that produces a document, so it
 * renders indented under whichever of those routes is selected rather than as
 * a dialog-wide setting.
 */
function RequestFormatField({ control }: { control: Control<RequestPickupFormValues> }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Controller
        control={control}
        name="format"
        render={({ field }) => (
          <TextField {...field} select size="small" label="Format" sx={{ width: 180 }}>
            {REQUEST_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Typography variant="caption" color="text.secondary">
        PDF to print or forward · CSV and Excel to work the numbers
      </Typography>
    </Stack>
  );
}

/**
 * One dialog for the whole "tell the vendor" step.
 *
 * Generating a document, emailing it, and recording a request made by phone are
 * not three separate actions — they are three answers to "how do you want to
 * send it", so they are one radio rather than three buttons competing on the
 * page. The shipment lands in REQUESTED either way.
 *
 * It leads with what the vendor will actually receive: before this, the only
 * way to know was to generate a file and open it.
 */
/**
 * What the vendor will actually receive, so the sender can check it before it
 * goes out.
 */
function RequestPreviewCard({ shipment }: { shipment: Shipment }) {
  // Lines are grouped by stream, unit, and snapshot signature. A stream stored
  // in two units renders as two rows; label those with the unit so they don't
  // read as duplicates. Signature-only splits keep the bare name — the unit is
  // not what distinguishes them.
  const streamUnits = new Map<string, Set<string>>();
  for (const line of shipment.manifestRequestLines) {
    const key = streamRowKey(line);
    const units = streamUnits.get(key) ?? new Set<string>();
    units.add(line.unit);
    streamUnits.set(key, units);
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          What the vendor sees
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {formatTotalsByUnit(shipment.totalQuantityByUnit)}
        </Typography>
      </Stack>
      {shipment.manifestRequestLines.map((line, i) => (
        <Stack
          key={`${line.wasteStream?.id ?? "uncharacterized"}-${line.unit}-${i}`}
          direction="row"
          spacing={1}
          alignItems="baseline"
          sx={{ py: 0.25 }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {line.wasteStream?.name ?? "Uncharacterized"}
            {(streamUnits.get(streamRowKey(line))?.size ?? 0) > 1 &&
              ` (${UNIT_LABELS[line.unit].toLowerCase()})`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {line.containerCount} {line.containerCount === 1 ? "container" : "containers"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatQuantity(line.quantity, line.unit)}
          </Typography>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary">
        Plus a per-container appendix for your records.
      </Typography>
    </Paper>
  );
}

/** How the request leaves Encamp — the choice that also commits the shipment. */
function RequestRouteField({
  control,
  isDraft,
}: {
  control: Control<RequestPickupFormValues>;
  isDraft: boolean;
}) {
  return (
    <Controller
      control={control}
      name="route"
      render={({ field }) => (
        <FormControl>
          <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>How do you want to send it?</FormLabel>
          {/* Said once, above the three routes, because all three make the
                same one-way move — and only from a draft, which is the only
                status where anything is lost. A draft can be deleted and a
                requested shipment can only be canceled, so this is the click
                that spends that, and none of the three options said so. */}
          {isDraft && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Any of these marks the shipment requested. After that it can be canceled but no longer
              deleted.
            </Typography>
          )}
          <RadioGroup {...field} sx={{ gap: 0.75 }}>
            <FormControlLabel
              value="email"
              sx={{ alignItems: "flex-start" }}
              control={<Radio size="small" sx={{ pt: 0.25 }} />}
              label={
                <Box>
                  <Typography variant="body2">Email it to the vendor now</Typography>
                  <Typography variant="caption" color="text.secondary">
                    They get the file attached, so nothing expires.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="download"
              sx={{ alignItems: "flex-start" }}
              control={<Radio size="small" sx={{ pt: 0.25 }} />}
              label={
                <Box>
                  <Typography variant="body2">Download it and send it myself</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Marks the shipment requested once the file is created.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="elsewhere"
              sx={{ alignItems: "flex-start" }}
              control={<Radio size="small" sx={{ pt: 0.25 }} />}
              label={
                <Box>
                  <Typography variant="body2">I already requested it another way</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Phoned in, or entered in the vendor&apos;s own portal. No document is created.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      )}
    />
  );
}

/**
 * Sending the request, by whichever route the user picked.
 *
 * Emailing is not all-or-nothing: the shipment flips to REQUESTED on the FIRST
 * recipient that succeeds, so a failure on a later one leaves the server ahead
 * of this page. Apollo runs refetchQueries only on success, which is why the
 * catch re-syncs by hand.
 */
function useRequestPickupSubmit({
  shipment,
  form,
  onClose,
}: {
  shipment: Shipment;
  form: UseFormReturn<RequestPickupFormValues>;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const { getValues, setError } = form;

  const [generate, { loading: generating }] = useMutation(GENERATE_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [emailRequest, { loading: emailing }] = useMutation(EMAIL_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [markRequested, { loading: marking }] = useMutation(MARK_SHIPMENT_REQUESTED_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const loading = generating || emailing || marking;

  const handleConfirm = async () => {
    if (loading) {
      return;
    }
    const values = getValues();
    // Validated on submit rather than by disabling the button: a disabled
    // button can't say why it is disabled, and it is unreachable to keyboard
    // and screen-reader users.
    if (values.route === "email" && values.recipients.length === 0) {
      setError("recipients", {
        type: "required",
        message: "Add at least one recipient to send the request.",
      });
      return;
    }
    try {
      if (values.route === "email") {
        await emailRequest({
          variables: {
            shipmentId: shipment.id,
            format: values.format,
            recipients: values.recipients,
          },
        });
        alerts.success(
          `Manifest request ${shipment.manifestRequestNumber} sent to ${values.recipients.length} ${
            values.recipients.length === 1 ? "recipient" : "recipients"
          }.`
        );
      } else if (values.route === "download") {
        const { data } = await generate({
          variables: { shipmentId: shipment.id, format: values.format },
        });
        const url = data?.generateManifestRequest.url;
        if (url) {
          openInNewTab(url);
        }
        alerts.success(`Manifest request generated for ${shipment.manifestRequestNumber}.`);
      } else {
        await markRequested({ variables: { shipmentId: shipment.id } });
        alerts.success(`${shipment.manifestRequestNumber} is marked requested.`);
      }
      onClose();
    } catch (e) {
      // Emailing is not all-or-nothing: the shipment flips to REQUESTED on the
      // FIRST recipient that succeeds, so a failure on a later one leaves the
      // server ahead of this page. Apollo runs refetchQueries only on success,
      // so without this the user is looking at DRAFT for a shipment that is
      // already requested, and only a reload would tell them otherwise.
      await client.refetchQueries({ include: REFETCH }).catch(() => {
        // The error below is the useful one; a failed re-sync must not replace it.
      });
      alerts.error(e instanceof Error ? e.message : "Failed to request the pickup.");
    }
  };

  return { handleConfirm, loading };
}

export function RequestPickupDialog({
  shipment,
  onClose,
}: {
  shipment: Shipment;
  onClose: () => void;
}) {
  const form = useForm<RequestPickupFormValues>({
    defaultValues: {
      route: "email",
      format: ManifestRequestFormat.Pdf,
      recipients: [],
    },
  });
  const { control, watch } = form;
  const route = watch("route");
  // Re-sending from REQUESTED costs nothing; it is the first move off a draft
  // that gives up the ability to delete.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const { handleConfirm, loading } = useRequestPickupSubmit({ shipment, form, onClose });
  const primaryLabel = PRIMARY_ACTION_LABEL[route];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Request pickup for ${shipment.manifestRequestNumber}`}
      primaryActionButtonProps={{
        saveText: primaryLabel,
        loading,
        disabled: loading,
        onClick: handleConfirm,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* What the request is, said once here rather than in an alert at the
              bottom: by the time a reader has reached the send button they have
              already committed, and each route states its own consequence.

              Deliberately silent on whether the shipment stays editable.
              Attaching the manifest the vendor sends back freezes the contents
              — `contentLockReasonFor` is what enforces it — so promising it can
              still be edited was a reassurance the flow does not keep. */}
          <Typography variant="body2" color="text.secondary">
            Your vendor gets this shipment summarized by waste stream. It is not an official
            manifest, so they create that and send it back. If details change before pickup, send an
            updated request so the vendor has the current version.
          </Typography>

          <RequestPreviewCard shipment={shipment} />

          <RequestRouteField control={control} isDraft={isDraft} />

          {/* Below the three options rather than indented under the selected
              one. Inline, the email route's recipients and format pushed the
              other two routes off the bottom of the dialog, so the question
              arrived with its answer apparently already given — and the way to
              discover there were alternatives was to scroll.
              The rule keeps them from reading as part of the last option they
              happen to sit under. Only the two routes that produce a document
              have anything to fill in. */}
          {route !== "elsewhere" && (
            <>
              <Divider />
              <Stack spacing={2}>
                {route === "email" && (
                  <EmailArrayField name="recipients" label="Vendor recipients" />
                )}
                <RequestFormatField control={control} />
              </Stack>
            </>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
